/**
 * Canonical task field option lists for the whole app. One source of truth so
 * the in-chat preview, multi-task modal, single-task modal, dashboard pills,
 * data view filters, etc. all show the SAME priorities & efforts.
 *
 *   PRIORITY_META     id (string) → { bg, text, label } (used for chip styling)
 *   PRIORITY_OPTIONS  ordered list for selects
 *   EFFORT_OPTIONS    ordered list for selects (value + minutes label)
 *   STATUS_META       legacy status string → { bg, text, label, stateKey }
 *
 * ⚠️ As of the V3 design-system unification, PRIORITY_META and STATUS_META
 * derive their COLOURS from `design-system/taskIdentity.js` so every legacy
 * consumer automatically renders V3 palette — no consumer changes needed.
 */
import {
  PRIORITY_V3,
  STATE_V3,
  resolveState,
  resolvePriority,
} from '../design-system/taskIdentity';

// Since V3 palettes now flow via CSS variables, the legacy bg is `var(...)`
// and not a hex string. The correct foreground is always the canonical
// inverse-text token — adapts automatically to light/dark mode.
function _pickTextColor() {
  return 'var(--orka-text-inverse)';
}

// PRIORITY_META — now a derived view over PRIORITY_V3. The shape is identical
// to the pre-V3 contract (bg/text/label) so every existing consumer keeps
// working; the ONLY change is that bg now comes from V3.
export const PRIORITY_META = Object.freeze(Object.fromEntries(
  Object.entries(PRIORITY_V3).map(([key, meta]) => [
    key,
    { bg: meta.color, text: _pickTextColor(meta.color), label: meta.label, long: meta.long },
  ]),
));

// Render order top→bottom in selects.
export const PRIORITY_OPTIONS = [
  { value: -1, key: "-1", label: 'P-1' },
  { value: 0,  key: "0",  label: 'P0' },
  { value: 1,  key: "1",  label: 'P1' },
  { value: 2,  key: "2",  label: 'P2' },
  { value: 3,  key: "3",  label: 'P3' },
  { value: 4,  key: "4",  label: 'P4' },
  { value: 5,  key: "5",  label: 'P5' },
];

// STATUS_META — derived view over STATE_V3 keyed by legacy status strings.
// Shape matches legacy contract { bg, text, label } plus a `stateKey` for
// consumers that want the V3 identifier.
const _LEGACY_STATUS_TO_STATE = {
  'To Do':       'open',
  'In Progress': 'inProgress',
  'Partial':     'partial',
  'Blocked':     'blocked',
  'Done':        'done',
};
export const STATUS_META = Object.freeze(Object.fromEntries(
  Object.entries(_LEGACY_STATUS_TO_STATE).map(([legacy, stateKey]) => {
    const meta = STATE_V3[stateKey];
    return [legacy, {
      bg: meta.color,
      text: _pickTextColor(meta.color),
      label: meta.label,
      short: meta.short,
      stateKey,
    }];
  }),
));

// Fibonacci-ish points → minutes mapping used everywhere.
export const EFFORT_OPTIONS = [
  { value: 1,  minutes: 15  },
  { value: 2,  minutes: 30  },
  { value: 3,  minutes: 60  },
  { value: 5,  minutes: 180 },
  { value: 8,  minutes: 360 },
  { value: 13, minutes: 600 },
];

export const formatEffortLabel = (e) => {
  const m = e.minutes;
  if (m < 60) return `E${e.value} (${m}m)`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  return rem ? `E${e.value} (${h}h ${rem}m)` : `E${e.value} (${h}h)`;
};

// Re-export resolvers for callers that want to skip the legacy shape.
export { resolveState, resolvePriority, PRIORITY_V3, STATE_V3 };
